/**
 * The StrategicClient (spec §3.1, §11.1; plan E14): the main thread's one handle on the strategic core, and the
 * only file under `src/strategic/**` that may touch `Worker` and comlink (spec §3.3).
 *
 * - **Modes.** `'worker'` starts the module worker (`src/worker/strategic.worker.ts`) and `wrap`s it with comlink;
 *   `'inProcess'` runs the same `StrategicHost` on the main thread (tests, `?worker=off`). The host module is
 *   imported lazily, so the core stays out of the main bundle unless the fallback runs.
 * - **Fallback** (spec §11.1, D-TBD: in-process fallback): if the worker cannot be constructed, reports an error
 *   before it answers, or does not answer a probe within `workerStartTimeoutMs` (3 s), the client terminates it,
 *   logs a warning and runs the host in-process. `mode` tells which one is running.
 * - **Cadence.** `tick(realSeconds)` is called every frame the Reach runs (the clock gate, plan I2). It only adds
 *   numbers until `advanceIntervalS` (strategic.json, 250 ms) has built up, then starts one pump: `advance` with
 *   exactly the time accumulated so far, then the new Wire items, then the system view (when dirty, at most every
 *   `systemViewIntervalS`) and the galaxy view (while someone subscribes to it, when dirty, at most every
 *   `galaxyViewIntervalS`). Only one pump is in flight at a time; time keeps accumulating meanwhile and goes out
 *   with the next one, so nothing is lost or sent twice. Nothing here ever blocks a frame.
 * - **Views.** The system view follows the watched system: `watchSystem(id)` if set, else the local system from
 *   `enterSystem`. The latest views, clock and the newest `wireKeep` Wire items are cached for the getters.
 * - **Wire.** `onWire` gets each new item once, in id order. Items that exist when `init` resolves (a loaded save)
 *   and items made by a catch-up are absorbed into the cache silently: they are history, and the away report
 *   carries the catch-up's headlines.
 * - **Errors.** Every host error, subscriber error and worker error after start goes to `onError`. Calls the
 *   caller awaits (`init`, `command`, …) also reject with it; pump errors only go to `onError`.
 * - **M6 (plan P15).** `command` also takes the M6 commands (`CoreCommand`); they go only to a host with the M6
 *   surface (`StrategicFleetHost`: the real host in both modes), recognised by a local copy of the M6 type names so
 *   the core stays out of the main bundle. The fleet, captain, corporation and player-station views and the fleet
 *   feed are fetched on request. Each advance's `fleetAlerts` go to `onFleetAlert` one by one, and its
 *   `walletDelta` goes to `onWalletDelta`; a delta that arrives with no subscriber is held and handed to the next
 *   one, so the Career applies every credit exactly once.
 */
import { proxy, wrap, type Endpoint, type Remote } from 'comlink'
import { logger } from '../core/logger'
import strategicJson from '../data/strategic/strategic.json'
import type { CatchUpPlayer } from './catchup'
import type { CoreCommand } from './commands'
import type { StrategicFleetHost } from './host'
import type {
  GateView,
  RelationsView,
  StandingDelta,
  GuardianKill,
  StoryReactivationNote,
  AdvanceResult,
  AwayReport,
  CaptainView,
  CommandResult,
  CorpView,
  FleetAlert,
  FleetLogEntry,
  FleetShipView,
  FleetView,
  GalaxyEconomyView,
  InitOptions,
  InitResult,
  LocalDelta,
  LocalManifest,
  LocalSync,
  PlayerStationView,
  CommissionView,
  StationView,
  StoryView,
  StrategicCommand,
  StrategicHost,
  StrategicSave,
  StrategicStats,
  SystemEconomyView,
  WireItem,
} from './types'

export type StrategicClientMode = 'worker' | 'inProcess'

/** The Reach clock as `advance` last reported it. */
export type ReachClock = AdvanceResult['clock']

/** What the client needs from a worker: a comlink endpoint it can terminate. */
export type WorkerLike = Endpoint & { terminate(): void }

/** Client timing (spec §11.1). The advance cadence and Wire cache size come from strategic.json. */
export interface StrategicClientTiming {
  /** Real seconds between `advance` calls (strategic.json `clock.advanceIntervalS`). */
  advanceIntervalS: number
  /** Minimum real seconds between system view fetches (spec §11.1: at most 1 Hz). */
  systemViewIntervalS: number
  /** Minimum real seconds between galaxy view fetches while subscribed (spec §11.1: every 10 s). */
  galaxyViewIntervalS: number
  /** The worker must answer a probe within this long or the client falls back (plan E14: 3 s). */
  workerStartTimeoutMs: number
  /** Wire items kept for the `wire` getter (strategic.json `wireKeep`). */
  wireKeep: number
}

/** The defaults: strategic.json where it has the value, the spec's numbers otherwise (D-TBD in the E14 report). */
export const DEFAULT_CLIENT_TIMING: Readonly<StrategicClientTiming> = Object.freeze({
  advanceIntervalS: strategicJson.clock.advanceIntervalS,
  systemViewIntervalS: 1,
  galaxyViewIntervalS: 10,
  workerStartTimeoutMs: 3000,
  wireKeep: strategicJson.wireKeep,
})

export interface StrategicClientOptions {
  mode: StrategicClientMode
  /** Receives every host, worker and subscriber error (see the file header). Defaults to `logger.error`. */
  onError?: (err: unknown) => void
  /** Overrides for the timing defaults (tests, the Reach monitor). */
  timing?: Partial<StrategicClientTiming>
  /** Wall clock for the in-process host's `stats()`; defaults to `performance.now()`. */
  now?: () => number
  /** Creates the worker (tests pass a fake endpoint); defaults to the module worker. */
  createWorker?: () => WorkerLike
  /** Creates the in-process host (tests pass a fake); defaults to the real `createStrategicHost({ now })`. */
  createHost?: () => StrategicHost | Promise<StrategicHost>
}

type Listener<T> = (value: T) => void
type Unsubscribe = () => void

export interface StrategicClient {
  /** The mode actually running: `'inProcess'` after a fallback. Settles when `ready` resolves. */
  readonly mode: StrategicClientMode
  /** Resolves once a host is running (after the worker probe or the fallback). */
  readonly ready: Promise<StrategicClientMode>
  /** True once `init` has resolved. */
  readonly initialized: boolean

  init(opts: InitOptions): Promise<InitResult>
  /** Called every frame the Reach runs with the real seconds that passed; batches `advance` (file header). */
  tick(realSeconds: number): void
  /** Resolves when the pump in flight (if any) has finished. */
  settle(): Promise<void>
  enterSystem(systemId: string): Promise<LocalManifest>
  exitSystem(): Promise<void>
  syncLocal(batch: readonly LocalSync[]): Promise<void>
  /** Any core command; an M6 command rejects when the host has no M6 surface (a test fake). */
  command(cmd: CoreCommand): Promise<CommandResult>
  catchUp(seconds: number, onProgress?: (day: number, days: number) => void, player?: CatchUpPlayer): Promise<AwayReport>
  save(): Promise<StrategicSave>
  stats(): Promise<StrategicStats>
  stationView(stationId: string): Promise<StationView>
  /** The system whose view is kept fresh: `id`, or the local system when null. */
  watchSystem(systemId: string | null): void
  /** Fetches the watched system's view now (null when no system is watched or local). */
  refreshSystemView(): Promise<SystemEconomyView | null>
  /** Fetches the galaxy view now. */
  refreshGalaxyView(): Promise<GalaxyEconomyView>
  /**
   * M8 TR-1b: one system's view by id, fetched now without touching the watched system or `systemView` (the price
   * log's covered-system refresh, once per online day).
   */
  fetchSystemView(systemId: string): Promise<SystemEconomyView>

  // M6 views (plan P15); each rejects when the host has no M6 surface.
  fleetView(): Promise<FleetView>
  /** One ship (`ps-<seq>`), null when unknown. */
  fleetShipView(shipId: string): Promise<FleetShipView | null>
  /** The roster at `stationId` (null = none), the hired captains and the memorial. */
  captainView(stationId: string | null): Promise<CaptainView>
  corpView(): Promise<CorpView>
  playerStationViews(): Promise<PlayerStationView[]>
  /** One commission (`pst-<seq>`), null when unknown. */
  playerStationView(commissionId: string): Promise<PlayerStationView | null>
  /** M6 P34: the commissioning view (charters, approved sites, caps, station type levels). */
  commissionView(): Promise<CommissionView>
  /** M7 (plan S16): the story view the main-thread story reads (`StoryWorldView`); rejects without the M6 surface. */
  storyView(): Promise<StoryView>
  /** The fleet feed, newest first (at most `limit`; 0 or less = all), optionally for one ship or captain. */
  fleetFeed(limit: number, filter?: { shipId?: string; captainId?: string }): Promise<FleetLogEntry[]>

  onDelta(fn: Listener<LocalDelta>): Unsubscribe
  onManifest(fn: Listener<LocalManifest>): Unsubscribe
  onSystemView(fn: Listener<SystemEconomyView>): Unsubscribe
  /** While at least one subscriber exists, the pump keeps the galaxy view fresh (a map is open). */
  onGalaxyView(fn: Listener<GalaxyEconomyView>): Unsubscribe
  onWire(fn: Listener<WireItem>): Unsubscribe
  onClock(fn: Listener<ReachClock>): Unsubscribe
  /** M5: relations and the player's standing, bounty and wanted rows, whenever the worker reports a change. */
  onRelations(fn: Listener<RelationsView>): Unsubscribe
  /** M5 (S-M2-13): standing deltas from an advance, for the Career to apply. */
  onStandingDeltas(fn: Listener<readonly StandingDelta[]>): Unsubscribe
  /** M7 §12.1 logging: guardian ships the player destroyed, from an advance, for the Career's log. */
  onGuardianKills(fn: Listener<readonly GuardianKill[]>): Unsubscribe
  /**
   * M7 (plan S16): online faction days that ended (`AdvanceResult.onlineDays`; catch-up days never count), for the
   * Career's `story.onlineDays`. Held until someone listens, like the wallet, so every day counts exactly once.
   */
  onOnlineDays(fn: Listener<number>): Unsubscribe
  /**
   * M7 S43: reactivation changes for the Career records (`AdvanceResult.storyReactivations`), in order. Held until
   * someone listens, like the online days, so no change is lost.
   */
  onStoryReactivations(fn: Listener<readonly StoryReactivationNote[]>): Unsubscribe
  /** M5: the gate stage, whenever it changes. */
  onGate(fn: Listener<GateView>): Unsubscribe
  /** The latest relations view (null before the first advance). */
  readonly relations: RelationsView | null
  /** Each fleet alert once, in the order the core raised it (M6). */
  onFleetAlert(fn: Listener<FleetAlert>): Unsubscribe
  /** Credits for the Career to add (+) or take (−), once per advance that has any; held until someone listens. */
  onWalletDelta(fn: Listener<number>): Unsubscribe

  readonly systemView: SystemEconomyView | null
  readonly galaxyView: GalaxyEconomyView | null
  readonly wire: readonly WireItem[]
  readonly clock: ReachClock | null
  readonly manifest: LocalManifest | null

  /** Stops the worker and drops every subscriber. Later calls reject. */
  dispose(): void
}

/** The module worker; the `new URL(..., import.meta.url)` form is what Vite bundles as a worker chunk. */
function defaultWorker(): WorkerLike {
  return new Worker(new URL('../worker/strategic.worker.ts', import.meta.url), { type: 'module' })
}

/** The M6 command types and dev ops: a copy of commands.ts `M6_COMMAND_TYPES`/`M6_DEV_OPS` (a test pins them equal). */
export const CLIENT_M6_COMMAND_TYPES: ReadonlySet<string> = new Set([
  'buyShip',
  'renameShip',
  'setOrder',
  'applyPreset',
  'setInsurance',
  'ferryShip',
  'transferService',
  'boardShip',
  'leaveShip',
  'loseBoardedShip',
  'hireCaptain',
  'fireCaptain',
  'assignCaptain',
  'payWages',
  'foundCorp',
  'setLivery',
  'companyTransfer',
  'buyCharter',
  'commissionStation',
  'upgradeStation',
  'setPricePolicy',
  'setPermissions',
  'setManager',
  'setClaimTariff',
  'setDockingRights',
])
export const CLIENT_M6_DEV_OPS: ReadonlySet<string> = new Set(['grantShip', 'grantCredits', 'forceFounding', 'forceShipLoss', 'forceDesertion', 'finishCommission', 'forceCaptainDeath', 'forceDisabledLoss'])

/** The M9 claimed-ship command types (plan CH-7a): a copy of commands.ts `CLAIMED_SHIP_COMMAND_TYPES` (a test pins them equal). */
export const CLIENT_CLAIMED_SHIP_COMMAND_TYPES: ReadonlySet<string> = new Set(['registerClaimedShip', 'setHullFlaws', 'scrapClaimedShip'])

/** M10 P5a11a: the cradle pair's command types (P5a10w): a copy of commands.ts `CARRIER_COMMAND_TYPES` (a test pins them equal). */
export const CLIENT_CARRIER_COMMAND_TYPES: ReadonlySet<string> = new Set(['launchFromCarrier', 'dockToCarrier'])

/** True for a command only the fleet surface takes: M6's, M9's claimed-ship commands (M9 `markClaim` is core) and M10's cradle pair. */
function isM6(cmd: CoreCommand): boolean {
  if (cmd.type === 'dev') return CLIENT_M6_DEV_OPS.has(cmd.op)
  return CLIENT_M6_COMMAND_TYPES.has(cmd.type) || CLIENT_CLAIMED_SHIP_COMMAND_TYPES.has(cmd.type) || CLIENT_CARRIER_COMMAND_TYPES.has(cmd.type)
}

/** True when the host has the M6 surface (the real host does; the tests' fakes may not). */
function isFleetHost(h: StrategicHost): h is StrategicFleetHost {
  return typeof (h as Partial<StrategicFleetHost>).fleetView === 'function'
}

/** A `StrategicFleetHost` over the comlink proxy (the progress callback crosses the boundary as a proxy). */
function remoteHost(remote: Remote<StrategicFleetHost>): StrategicFleetHost {
  return {
    init: (o) => remote.init(o),
    advance: (s) => remote.advance(s),
    command: (c) => remote.command(c),
    enterSystem: (id) => remote.enterSystem(id),
    exitSystem: () => remote.exitSystem(),
    syncLocal: (b) => remote.syncLocal(b),
    systemView: (id) => remote.systemView(id),
    stationView: (id) => remote.stationView(id),
    galaxyView: () => remote.galaxyView(),
    wireSince: (after) => remote.wireSince(after),
    catchUp: (seconds, onProgress, player) => remote.catchUp(seconds, onProgress === undefined ? undefined : proxy(onProgress), player),
    save: () => remote.save(),
    stats: () => remote.stats(),
    fleetView: () => remote.fleetView(),
    fleetShipView: (id) => remote.fleetShipView(id),
    captainView: (id) => remote.captainView(id),
    corpView: () => remote.corpView(),
    storyView: () => remote.storyView(),
    playerStationViews: () => remote.playerStationViews(),
    playerStationView: (id) => remote.playerStationView(id),
    commissionView: () => remote.commissionView(),
    fleetFeed: (limit, filter) => remote.fleetFeed(limit, filter),
  }
}

async function inProcessHost(now: () => number): Promise<StrategicHost> {
  const { createStrategicHost } = await import('./host')
  return createStrategicHost({ now })
}

/** Creates the client and starts its host (see the file header). */
export function createStrategicClient(opts: StrategicClientOptions): StrategicClient {
  const timing: StrategicClientTiming = { ...DEFAULT_CLIENT_TIMING, ...opts.timing }
  const now = opts.now ?? ((): number => performance.now())
  const reportError = opts.onError ?? ((err: unknown): void => logger.error('strategic client error', err))

  let mode: StrategicClientMode = opts.mode
  let worker: WorkerLike | null = null
  let disposed = false
  let initialized = false

  // Pump state: real seconds waiting to go out, real seconds since the last advance, one pump at a time.
  let pendingS = 0
  let sinceAdvanceS = 0
  let realS = 0
  let pump: Promise<void> | null = null
  let systemFetchedAt = Number.NEGATIVE_INFINITY
  let galaxyFetchedAt = Number.NEGATIVE_INFINITY
  let forceSystem = false

  // Caches.
  let localSystem: string | null = null
  let watchedSystem: string | null = null
  let systemView: SystemEconomyView | null = null
  let galaxyView: GalaxyEconomyView | null = null
  let clock: ReachClock | null = null
  let relations: RelationsView | null = null
  let manifest: LocalManifest | null = null
  let lastWireId = -1
  const wire: WireItem[] = []

  const deltaFns = new Set<Listener<LocalDelta>>()
  const manifestFns = new Set<Listener<LocalManifest>>()
  const systemFns = new Set<Listener<SystemEconomyView>>()
  const galaxyFns = new Set<Listener<GalaxyEconomyView>>()
  const wireFns = new Set<Listener<WireItem>>()
  const clockFns = new Set<Listener<ReachClock>>()
  const relationFns = new Set<Listener<RelationsView>>()
  const standingFns = new Set<Listener<readonly StandingDelta[]>>()
  const guardianKillFns = new Set<Listener<readonly GuardianKill[]>>()
  const gateFns = new Set<Listener<GateView>>()
  const alertFns = new Set<Listener<FleetAlert>>()
  const walletFns = new Set<Listener<number>>()
  /** Wallet credits that arrived while nobody listened (handed to the next `onWalletDelta` subscriber). */
  let heldWallet = 0
  const onlineDayFns = new Set<Listener<number>>()
  let heldOnlineDays = 0
  const reactivationFns = new Set<Listener<readonly StoryReactivationNote[]>>()
  const heldReactivations: StoryReactivationNote[] = []

  function fail(err: unknown): void {
    try {
      reportError(err)
    } catch (e) {
      logger.error('strategic client: onError threw', e)
    }
  }

  function emit<T>(fns: Set<Listener<T>>, value: T): void {
    for (const fn of fns) {
      try {
        fn(value)
      } catch (err) {
        fail(err)
      }
    }
  }

  function subscribe<T>(fns: Set<Listener<T>>, fn: Listener<T>): Unsubscribe {
    fns.add(fn)
    return () => {
      fns.delete(fn)
    }
  }

  function inProcess(): Promise<StrategicHost> {
    return opts.createHost !== undefined ? Promise.resolve(opts.createHost()) : inProcessHost(now)
  }

  function fallback(reason: string, err?: unknown): Promise<StrategicHost> {
    logger.warn(`strategic client: ${reason}; running the Reach in-process`, err)
    if (worker !== null) {
      worker.terminate()
      worker = null
    }
    mode = 'inProcess'
    return inProcess()
  }

  /** Starts the worker and waits for it to answer a probe, or falls back. */
  function startWorker(): Promise<StrategicHost> {
    let w: WorkerLike
    try {
      w = (opts.createWorker ?? defaultWorker)()
    } catch (err) {
      return fallback('the strategic worker could not be created', err)
    }
    worker = w
    const remote = wrap<StrategicFleetHost>(w)
    const host = remoteHost(remote)
    return new Promise<StrategicHost>((resolve) => {
      let started = false
      let settled = false
      const finish = (h: Promise<StrategicHost> | StrategicHost): void => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(h)
      }
      const onWorkerError = (ev: Event): void => {
        if (!started) finish(fallback('the strategic worker failed to start', ev))
        else fail(ev)
      }
      w.addEventListener('error', onWorkerError as EventListener)
      w.addEventListener('messageerror', onWorkerError as EventListener)
      const timer = setTimeout(() => finish(fallback(`the strategic worker did not answer within ${timing.workerStartTimeoutMs} ms`)), timing.workerStartTimeoutMs)
      // Any answer proves the worker runs; before init the host rejects stats(), which is an answer too.
      const answered = (): void => {
        if (settled) return
        started = true
        finish(host)
      }
      host.stats().then(answered, answered)
    })
  }

  const hostReady: Promise<StrategicHost> = opts.mode === 'worker' ? startWorker() : inProcess()
  const ready: Promise<StrategicClientMode> = hostReady.then(() => mode)

  /** Awaits the host and runs `fn`, reporting and rethrowing any error. */
  async function call<T>(fn: (h: StrategicHost) => Promise<T>): Promise<T> {
    if (disposed) throw new Error('strategic client: disposed')
    const h = await hostReady
    try {
      return await fn(h)
    } catch (err) {
      fail(err)
      throw err
    }
  }

  /** Awaits the host and runs `fn` on its M6 surface; rejects (and reports) when the host has none. */
  function callFleet<T>(fn: (h: StrategicFleetHost) => Promise<T>): Promise<T> {
    return call((h) => (isFleetHost(h) ? fn(h) : Promise.reject(new Error('strategic client: the host has no M6 fleet surface'))))
  }

  function emitReactivations(notes: readonly StoryReactivationNote[]): void {
    if (reactivationFns.size === 0) {
      for (const n of notes) heldReactivations.push(n)
      return
    }
    emit(reactivationFns, notes)
  }

  function emitOnlineDays(days: number): void {
    if (onlineDayFns.size === 0) {
      heldOnlineDays += days
      return
    }
    emit(onlineDayFns, days)
  }

  function emitWallet(delta: number): void {
    if (walletFns.size === 0) {
      heldWallet += delta
      return
    }
    emit(walletFns, delta)
  }

  /** Appends Wire items newer than the last one seen, in order, and emits them unless `silent`. */
  function absorbWire(items: readonly WireItem[], silent: boolean): void {
    for (const item of items) {
      if (item.id <= lastWireId) continue
      lastWireId = item.id
      wire.push(item)
      if (!silent) emit(wireFns, item)
    }
    const excess = wire.length - timing.wireKeep
    if (excess > 0) wire.splice(0, excess)
  }

  function watched(): string | null {
    return watchedSystem ?? localSystem
  }

  async function fetchSystemView(h: StrategicHost): Promise<SystemEconomyView | null> {
    const id = watched()
    if (id === null) return null
    systemFetchedAt = realS
    forceSystem = false
    const view = await h.systemView(id)
    // A watch change while the fetch was in flight makes this view stale.
    if (watched() !== id) return null
    systemView = view
    emit(systemFns, view)
    return view
  }

  async function fetchGalaxyView(h: StrategicHost): Promise<GalaxyEconomyView> {
    galaxyFetchedAt = realS
    const view = await h.galaxyView()
    galaxyView = view
    emit(galaxyFns, view)
    return view
  }

  async function runPump(seconds: number): Promise<void> {
    const h = await hostReady
    const r = await h.advance(seconds)
    if (disposed) return
    clock = r.clock
    emit(clockFns, r.clock)
    if (r.delta !== null) emit(deltaFns, r.delta)
    if (r.relations !== undefined) {
      relations = r.relations
      emit(relationFns, r.relations)
    }
    if (r.standingDeltas !== undefined && r.standingDeltas.length > 0) emit(standingFns, r.standingDeltas)
    if (r.guardianKills !== undefined && r.guardianKills.length > 0) emit(guardianKillFns, r.guardianKills)
    if (r.onlineDays !== undefined && r.onlineDays > 0) emitOnlineDays(r.onlineDays)
    if (r.storyReactivations !== undefined && r.storyReactivations.length > 0) emitReactivations(r.storyReactivations)
    if (r.gate !== undefined) emit(gateFns, r.gate)
    if (r.walletDelta !== undefined && r.walletDelta !== 0) emitWallet(r.walletDelta)
    if (r.fleetAlerts !== undefined) for (const a of r.fleetAlerts) emit(alertFns, a)
    if (r.wireHead > lastWireId) absorbWire(await h.wireSince(lastWireId), false)
    if (forceSystem || (r.dirty.system && realS - systemFetchedAt >= timing.systemViewIntervalS)) await fetchSystemView(h)
    if (galaxyFns.size > 0 && (galaxyView === null || (r.dirty.galaxy && realS - galaxyFetchedAt >= timing.galaxyViewIntervalS))) {
      await fetchGalaxyView(h)
    }
  }

  function startPump(): void {
    const seconds = pendingS
    pendingS = 0
    sinceAdvanceS = 0
    pump = runPump(seconds)
      .catch(fail)
      .finally(() => {
        pump = null
      })
  }

  return {
    get mode() {
      return mode
    },
    ready,
    get initialized() {
      return initialized
    },

    async init(o: InitOptions): Promise<InitResult> {
      const r = await call((h) => h.init(o))
      pendingS = 0
      sinceAdvanceS = 0
      localSystem = null
      systemView = null
      galaxyView = null
      clock = null
      manifest = null
      lastWireId = -1
      wire.length = 0
      absorbWire(await call((h) => h.wireSince(-1)), true)
      initialized = true
      return r
    },

    tick(realSeconds: number): void {
      if (!initialized || disposed || !(realSeconds > 0) || !Number.isFinite(realSeconds)) return
      pendingS += realSeconds
      sinceAdvanceS += realSeconds
      realS += realSeconds
      if (pump === null && sinceAdvanceS >= timing.advanceIntervalS) startPump()
    },

    async settle(): Promise<void> {
      while (pump !== null) await pump
    },

    async enterSystem(systemId: string): Promise<LocalManifest> {
      const m = await call((h) => h.enterSystem(systemId))
      localSystem = m.systemId
      manifest = m
      emit(manifestFns, m)
      // The host notes the local system as seen on entry, so fetch its view now rather than wait for dirt.
      if (watchedSystem === null) await call(fetchSystemView)
      return m
    },

    async exitSystem(): Promise<void> {
      await call((h) => h.exitSystem())
      localSystem = null
      manifest = null
    },

    syncLocal: (batch) => call((h) => h.syncLocal(batch)),
    command: (cmd) =>
      call((h) => {
        if (isFleetHost(h)) return h.command(cmd)
        if (isM6(cmd)) return Promise.reject(new Error(`strategic client: the host has no M6 fleet surface for "${cmd.type}"`))
        return h.command(cmd as StrategicCommand)
      }),

    async catchUp(seconds, onProgress, player): Promise<AwayReport> {
      const report = await call((h) => h.catchUp(seconds, onProgress, player))
      // catchUp leaves the local system (spec §10.1 step 5).
      localSystem = null
      manifest = null
      forceSystem = watchedSystem !== null
      absorbWire(await call((h) => h.wireSince(lastWireId)), true)
      return report
    },

    save: () => call((h) => h.save()),
    stats: () => call((h) => h.stats()),
    stationView: (id) => call((h) => h.stationView(id)),

    watchSystem(systemId: string | null): void {
      if (systemId === watchedSystem) return
      watchedSystem = systemId
      systemView = null
      forceSystem = watched() !== null
    },

    refreshSystemView: () => call(fetchSystemView),
    refreshGalaxyView: () => call(fetchGalaxyView),
    fetchSystemView: (id) => call((h) => h.systemView(id)),

    fleetView: () => callFleet((h) => h.fleetView()),
    fleetShipView: (id) => callFleet((h) => h.fleetShipView(id)),
    captainView: (id) => callFleet((h) => h.captainView(id)),
    corpView: () => callFleet((h) => h.corpView()),
    storyView: () => callFleet((h) => h.storyView()),
    playerStationViews: () => callFleet((h) => h.playerStationViews()),
    playerStationView: (id) => callFleet((h) => h.playerStationView(id)),
    commissionView: () => callFleet((h) => h.commissionView()),
    fleetFeed: (limit, filter) => callFleet((h) => h.fleetFeed(limit, filter)),

    onDelta: (fn) => subscribe(deltaFns, fn),
    onManifest: (fn) => subscribe(manifestFns, fn),
    onSystemView: (fn) => subscribe(systemFns, fn),
    onGalaxyView: (fn) => subscribe(galaxyFns, fn),
    onWire: (fn) => subscribe(wireFns, fn),
    onClock: (fn) => subscribe(clockFns, fn),
    onRelations: (fn) => subscribe(relationFns, fn),
    onStandingDeltas: (fn) => subscribe(standingFns, fn),
    onGuardianKills: (fn) => subscribe(guardianKillFns, fn),
    onOnlineDays(fn: Listener<number>): Unsubscribe {
      const off = subscribe(onlineDayFns, fn)
      if (heldOnlineDays > 0) {
        const held = heldOnlineDays
        heldOnlineDays = 0
        emit(onlineDayFns, held)
      }
      return off
    },
    onStoryReactivations(fn: Listener<readonly StoryReactivationNote[]>): Unsubscribe {
      const off = subscribe(reactivationFns, fn)
      if (heldReactivations.length > 0) emit(reactivationFns, heldReactivations.splice(0, heldReactivations.length))
      return off
    },
    onGate: (fn) => subscribe(gateFns, fn),
    onFleetAlert: (fn) => subscribe(alertFns, fn),
    onWalletDelta(fn: Listener<number>): Unsubscribe {
      const off = subscribe(walletFns, fn)
      if (heldWallet !== 0) {
        const held = heldWallet
        heldWallet = 0
        emit(walletFns, held)
      }
      return off
    },

    get systemView() {
      return systemView
    },
    get galaxyView() {
      return galaxyView
    },
    get wire() {
      return wire
    },
    get clock() {
      return clock
    },
    get relations() {
      return relations
    },
    get manifest() {
      return manifest
    },

    dispose(): void {
      disposed = true
      initialized = false
      for (const fns of [deltaFns, manifestFns, systemFns, galaxyFns, wireFns, clockFns, relationFns, standingFns, guardianKillFns, gateFns, alertFns, walletFns, onlineDayFns, reactivationFns] as Set<unknown>[]) fns.clear()
      if (worker !== null) {
        worker.terminate()
        worker = null
      }
    },
  }
}
